// DoctorSearchKit.jsx — v242.103.0
//
// The Doctors directory and the in-case specialist picker are two screens doing
// one job, and until now they looked nothing alike: the directory had a location
// bar, five filter chips, a List/Map toggle and a compact card; the picker had a
// one-line location button, three boolean chips and a large gradient card.
// Dr. Rami's call (2026-08-20): the picker adopts the directory's look.
//
// ★★ THE POINT OF THIS FILE IS THAT THERE IS ONE COPY. Building the picker's new
//    chrome by pasting the directory's markup would have made the two screens
//    identical for exactly as long as it took someone to edit one of them — which
//    is the shape this project has paid for repeatedly (REQUIRED-TABLE-DUP-1,
//    CASETRANSFORM-DUP-1, and the sidebar count that disagreed with its own
//    children). These components are MOVED here, not retyped, so the directory
//    renders the same bytes it did before.
//
// ★ `DoctorCard` was already exported from DoctorsScreen; it now lives here so
//   importing it does not drag a 774-line screen (and its Google Maps loader)
//   into the picker's bundle.
import { createPortal } from 'react-dom'
import {
  MapPin, ChevronDown, ChevronRight, Star, ShieldCheck, CheckCircle2, DollarSign, Heart,
  Video, SlidersHorizontal, LocateFixed, List, Map as MapIcon, Lock,
} from 'lucide-react'
import { useI18n } from '../../i18n'
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { getInsuranceMatchType, patientHasInsurance as hasInsuranceFn } from '../../utils/insuranceMatch'

/* ===== moved verbatim from DoctorsScreen (v242.103.0) ===== */
export const SPECIALTY_FILTERS = [
  'Allergy & Immunology', 'Anesthesiology', 'Audiology', 'Cardiology',
  'Dental', 'Dermatology', 'Endocrinology', 'Gastroenterology',
  'General Medicine', 'Hematology', 'Infectious Disease',
  'Nephrology', 'Neurology', 'Obstetrics & Gynecology',
  'Oncology', 'Ophthalmology', 'Orthopedics', 'Otolaryngology (ENT)',
  'Pain Management', 'Physical Medicine & Rehab',
  'Plastic Surgery', 'Podiatry', 'Psychiatry', 'Pulmonology',
  'Rheumatology', 'Sleep Medicine', 'Sports Medicine',
  'Urology', 'Vascular Surgery',
]

export const DISTANCE_OPTIONS = [5, 10, 25, 50]


export function formatNextSlot(slot) {
  if (!slot) return ''
  const dateStr = slot.date || slot.start || ''
  const [y, mo, dy] = (dateStr || '').split('-').map(Number)
  if (!y) return ''
  const d = new Date(y, mo - 1, dy)
  const today = new Date()
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1)
  const dayLabel = d.toDateString() === today.toDateString() ? 'Today'
    : d.toDateString() === tomorrow.toDateString() ? 'Tomorrow'
    : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const rawTime = slot.time || (Array.isArray(slot.times) ? slot.times[0] : null)
  let timeStr = ''
  if (rawTime && /^\d{1,2}:\d{2}$/.test(rawTime)) {
    const [h, m] = rawTime.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    timeStr = `${h12}:${String(m).padStart(2, '0')} ${ampm}`
  }
  return `${dayLabel}${timeStr ? ` ${timeStr}` : ''}`
}

/* ===== Filter sheet helpers (v230.8.0) ===== */
export function FilterGroup({ label, children }) {
  return (
    <div className="mb-4">
      <p className="text-[11px] font-extrabold tracking-wide uppercase mb-2" style={{ color: '#5F7A8E' }}>{label}</p>
      <div className="flex gap-2 flex-wrap">{children}</div>
    </div>
  )
}
export function FilterOpt({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className="text-[12.5px] font-bold rounded-[10px] px-3 py-2 transition-colors"
      style={active ? { border: '1px solid #46A8F9', color: '#46A8F9' } : { border: '1px solid #E7EEF4', color: '#15354F' }}
    >
      {children}
    </button>
  )
}


/* ===== Doctor Card (v230.7.0 redesign) ===== */
export function DoctorCard({ doc, isMyDoctor, isFavorite = false, insurance, onTap, isSelected = false, onViewProfile = null }) {
  const { t } = useI18n()
  const initials = `${doc.first_name?.[0] || ''}${doc.last_name?.[0] || ''}`
  const hasVirtual = (doc.appointment_types || []).some(t => t?.includes?.('virtual')) ||
    doc.appointmentTypes?.newVirtual || doc.appointmentTypes?.followupVirtual
  const creds = typeof doc.credentials === 'string' ? doc.credentials : (doc.credentials?.text || '')
  // ★★★ `matchedSpecialty` FIRST, AND THIS IS TF-21 RETURNING (2026-08-20).
  //     matchDoctorsToCase tags each doctor with the specialty that EARNED the
  //     match. A doctor listing 29 specialties matches an ENT case on ENT and has
  //     "Cardiology" at specialties[0] — so rendering [0] told the patient their
  //     ENT case had matched a cardiologist. The DOCTORS were right; the LABEL lied.
  //     ShareScreen's own comment warns of exactly this: "TF-21 came from displaying
  //     a different field than the one matched on; two implementations is how that
  //     returns." The directory sets no matchedSpecialty, so it is unaffected.
  const specialty = (doc.matchedSpecialty || doc.specialties?.[0] || doc.specialty || '')
  const matchType = !hasInsuranceFn(insurance) ? 'none' : getInsuranceMatchType(doc, insurance)
  const nextLabel = formatNextSlot(doc._nextSlot)

  // v235.7.1 — self-pay patients see the doctor's cash price in place of the
  // (suppressed) network badge. spMin = lowest listed rate → "from $X".
  const isSelfPayPatient = !!insurance?.selfPay
  const docAcceptsSelfPay = doc.accept_self_pay !== false
  const _spRates = [doc.self_pay_in_office_rate, doc.self_pay_virtual_rate]
    .filter((r) => r != null && !isNaN(Number(r)))
    .map(Number)
  const spMin = _spRates.length ? Math.min(..._spRates) : null

  return (
    <button
      onClick={onTap}
      className="w-full flex items-center gap-3 p-3.5 rounded-2xl text-left transition-colors active:bg-blue-50"
      style={{ background: isSelected ? '#F4FBFF' : '#fff', border: `1px solid ${isSelected ? '#46A8F9' : '#E7EEF4'}` }}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden"
        style={{ background: '#fff', border: `1.5px solid ${isMyDoctor ? '#46A8F9' : '#A7D9FC'}` }}>
        {doc.profile_photo ? (
          <img src={doc.profile_photo} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="font-bold text-sm" style={{ color: '#46A8F9' }}>{initials}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        {/* v242.202.0 — TEST 1.18. The favourite flag was COMPUTED and never drawn:
            MyDoctorsScreen built `_isFavorite` for every row and read it in exactly one
            place, the Favorites filter. On the All tab — the tab the screen opens on — a
            doctor the patient had deliberately saved looked identical to one who is only
            in the list because a case once touched them.
            ★ THE IDIOM IS NOT NEW. DoctorProfileScreen's Save control already says
              "favourite" with a filled Heart at #46A8F9; this is that same mark, smaller,
              beside the name. Inventing a second visual language for one flag is how two
              screens start disagreeing about what saving a doctor looks like.
            ★★ AND IT CARRIES NO NEW COPY. The app has ELEVEN locales and only en + es
              hold the `doctors.*` keys this screen uses, so any new sentence would ship
              untranslated in nine of them. The mark is an icon; its accessible name
              reuses `doctors.favorites`, the key the Favorites chip above the list
              already uses. No locale file is touched. */}
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="font-bold text-[14.5px] truncate" style={{ color: '#15354F' }}>
            Dr. {doc.first_name} {doc.last_name}{creds ? `, ${creds}` : ''}
          </p>
          {isFavorite && (
            <Heart
              size={13}
              aria-label={t('doctors.favorites', { defaultValue: 'Favorites' })}
              className="flex-shrink-0 fill-[#46A8F9] text-[#46A8F9]"
            />
          )}
        </div>
        <p className="text-[12.5px] font-semibold mt-0.5 truncate" style={{ color: '#15354F' }}>{formatProviderSpecialty(specialty, t)}</p>
        <div className="flex items-center gap-2.5 mt-1 flex-wrap text-[11.5px] font-medium" style={{ color: '#64748B' }}>
          {doc.google_rating && (
            <span className="flex items-center gap-0.5">
              <Star size={11} className="fill-amber-400 text-amber-400" />
              <span style={{ color: '#15354F', fontWeight: 700 }}>{doc.google_rating}</span>
              {doc.google_review_count ? ` (${doc.google_review_count})` : ''}
            </span>
          )}
          {doc._distance != null && (
            <span>{doc.google_rating ? '· ' : ''}{doc._distance} mi</span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1.5 flex-wrap text-[11px] font-bold">
          {/* v235.7.1 — self-pay patients see cash price instead of network status */}
          {isSelfPayPatient && docAcceptsSelfPay && spMin != null && (
            <span className="flex items-center gap-1" style={{ color: '#15803D' }}><DollarSign size={12} /> Self-pay from ${spMin}</span>
          )}
          {isSelfPayPatient && docAcceptsSelfPay && spMin == null && (
            <span className="flex items-center gap-1" style={{ color: '#64748B' }}><DollarSign size={12} /> {t('doctors.acceptsSelfPay', 'Accepts self-pay')}</span>
          )}
          {!isSelfPayPatient && matchType === 'confirmed' && (
            <span className="flex items-center gap-1" style={{ color: '#15803D' }}><CheckCircle2 size={12} /> {t('doctors.inNetwork')}</span>
          )}
          {!isSelfPayPatient && matchType === 'no_match' && (
            <span style={{ color: '#15354F' }}>{t('doctors.outOfNetwork', 'Out-of-network')}</span>
          )}
          {!isSelfPayPatient && matchType === 'unknown' && (
            <span style={{ color: '#64748B' }}>{t('doctors.networkUnverified', 'Network unverified')}</span>
          )}
          {doc._noOnlineBooking && (
            // ★ s114 B4 (Dr. Rami 2026-10-22 build-80 bugs): a physician without bookable hours is LISTED, never hidden.
            // ★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116): the badge says what the patient can do,
            //   doctors.requestTimeBadge (approved en + es); share.noAppointmentsBadge stays for its other readers.
            <span data-no-online-booking style={{ color: '#9A5B00' }}>{t('doctors.requestTimeBadge')}</span>
          )}
          {nextLabel && (
            <span className="flex items-center gap-1.5" style={{ color: '#15354F' }}>
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#66D170' }} /> {nextLabel}
            </span>
          )}
          {hasVirtual && (
            <span className="flex items-center gap-1" style={{ color: '#46A8F9' }}><Video size={11} /> {t('caseHome.virtual', 'Virtual')}</span>
          )}
        </div>
        {/* v242.103.0 — in the PICKER the row commits (Dr. Rami: "tap the row"), so
            the profile needs its own affordance and the chevron would be a lie:
            it promises navigation on a row that selects.
            ★★ THIS MAY NOT BE A <button>. The card's own root is one, and a nested
               button is un-nested by the browser at parse time however cleanly React
               renders it (ORELLA_RULES §1bf(e)). role="link" + stopPropagation is the
               affordance without the nesting. */}
        {onViewProfile && (
          <span
            role="link"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onViewProfile(doc) }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); onViewProfile(doc) } }}
            className="inline-block mt-1.5 text-[11.5px] font-extrabold"
            style={{ color: '#46A8F9' }}
          >
            {t('doctors.viewProfileLink', { defaultValue: 'View profile ›' })}
          </span>
        )}
      </div>
      {!onViewProfile && <ChevronRight size={16} className="flex-shrink-0" style={{ color: '#C8D6E2' }} />}
    </button>
  )
}

/* ===== search chrome — MOVED from DoctorsScreen, not retyped (v242.103.0) =====
   Each body below is the directory's own JSX. The props are named exactly like
   the locals they replaced, so the markup inside is textually unchanged and the
   directory keeps rendering what it rendered before. ★ That is the whole reason
   these are props and not a tidier API. */

export function DoctorLocationBar({ userLocation, setShowLoc }) {
  const { t } = useI18n()
  return (
    <button onClick={() => setShowLoc(true)} className="w-full flex items-center gap-2.5 rounded-[14px] px-3 py-2.5 mb-3 text-left" style={{ background: '#F4FBFF', border: '1.5px solid #A7D9FC' }}>
      <MapPin size={18} style={{ color: '#46A8F9', flexShrink: 0 }} />
      <span className="flex flex-col min-w-0">
        <span className="text-[9.5px] font-bold tracking-wider" style={{ color: '#94A3B8' }}>{t('doctors.showingDoctorsNear', { defaultValue: 'SHOWING DOCTORS NEAR' })}</span>
        <span className="text-[13.5px] font-extrabold truncate" style={{ color: '#15354F' }}>{userLocation?.label || t('doctors.yourArea', { defaultValue: 'Your area' })}</span>
      </span>
      <ChevronDown size={17} className="ml-auto flex-shrink-0" style={{ color: '#46A8F9' }} />
    </button>
  )
}

export function DoctorFilterChips({
  setShowFilters, distanceFilter, specialtyFilter, patientHasInsurance,
  insuranceActive, setInsuranceActive, virtualActive, setVirtualActive, activeFilterCount,
  specialtyLocked = false, lockedSpecialtyLabel = null,
  specialtyChoices = null, specialtyChoiceId = null,
  orellaOnly = false, setOrellaOnly = null,
}) {
  const { t } = useI18n()
  // s101 (Build 67): the picker's specialty is a CHOICE now - the case's admin-assigned specialties
  // plus Primary care (utils/specialtyChoices). The chip shows it and opens the sheet.
  const choice = Array.isArray(specialtyChoices) ? (specialtyChoices.find((c) => c.id === specialtyChoiceId) || specialtyChoices[0]) : null
  const choiceLabel = choice ? (choice.primary ? t('doctors.primaryCare', 'Primary care') : choice.label) : null
  return (
            <div className="flex gap-2 mb-3 items-center overflow-x-auto pb-0.5">
    <button
      onClick={() => setShowFilters(true)}
      className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${distanceFilter ? 'text-white' : ''}`}
      style={distanceFilter ? { background: '#46A8F9' } : { background: '#fff', border: '1px solid #A7D9FC', color: '#46A8F9' }}
    >
      <LocateFixed size={12} /> {distanceFilter ? t('doctors.withinMi', { defaultValue: 'Within {{miles}} mi', miles: distanceFilter }) : t('doctors.distance', { defaultValue: 'Distance' })} <ChevronDown size={12} />
    </button>

    {/* ★ In the PICKER the case has already chosen the specialty, so a chip that
        opens a picker offers to undo the case. Locked renders the same chip shape,
        inert, so the row still matches the directory and still tells the truth. */}
    {/* ★ v242.206.0 (TEST 1.34) — a label that is not a string falls back to the
        generic chip text; rendering an object here is React #31. */}
    {choice ? (
      <button
        onClick={() => setShowFilters(true)}
        data-cta="picker-specialty-chip"
        className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 text-white"
        style={{ background: '#15354F' }}
      >
        {choiceLabel} <ChevronDown size={12} />
      </button>
    ) : specialtyLocked ? (
      <span
        className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
        style={{ background: '#F1F5F8', border: '1px solid #E2E8F0', color: '#7C8FA3' }}
      >
        {(typeof lockedSpecialtyLabel === 'string' && lockedSpecialtyLabel) || t('doctors.specialty', { defaultValue: 'Specialty' })}
      </span>
    ) : (
    <button
      onClick={() => setShowFilters(true)}
      className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 flex-shrink-0 ${specialtyFilter ? 'text-white' : ''}`}
      style={specialtyFilter ? { background: '#15354F' } : { background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}
    >
      {specialtyFilter || t('doctors.specialty', { defaultValue: 'Specialty' })} <ChevronDown size={12} />
    </button>
    )}

    {patientHasInsurance && (
      <button
        onClick={() => setInsuranceActive(!insuranceActive)}
        className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 flex-shrink-0 ${insuranceActive ? 'text-white' : ''}`}
        style={insuranceActive ? { background: '#15354F' } : { background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}
      >
        <ShieldCheck size={11} /> {t('doctors.inNetwork', { defaultValue: 'In-network' })}
      </button>
    )}

    <button
      onClick={() => setVirtualActive(!virtualActive)}
      className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 flex-shrink-0 ${virtualActive ? 'text-white' : ''}`}
      style={virtualActive ? { background: '#15354F' } : { background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}
    >
      <Video size={11} /> {t('doctors.virtual')}
    </button>

    {/* s123 (board A): "On Orella" — only clinicians on Orella. Shown where the caller owns the switch (the Doctors tab). */}
    {setOrellaOnly && (
      <button
        onClick={() => setOrellaOnly(!orellaOnly)}
        data-cta="doctors-orella-only"
        aria-pressed={orellaOnly}
        className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 flex-shrink-0 ${orellaOnly ? 'text-white' : ''}`}
        style={orellaOnly ? { background: '#15354F' } : { background: '#fff', border: '1px solid #E7EEF4', color: '#15354F' }}
      >
        {t('doctors.showOrella', { defaultValue: 'On Orella' })}
      </button>
    )}

    <button
      onClick={() => setShowFilters(true)}
      className="px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
      style={{ background: '#fff', border: '1px solid #E7EEF4', color: '#64748B' }}
    >
      <SlidersHorizontal size={12} /> {t('doctors.filters', { defaultValue: 'Filters' })}
      {activeFilterCount > 0 && (
        <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[10px] font-extrabold text-white" style={{ background: '#46A8F9' }}>{activeFilterCount}</span>
      )}
    </button>
            </div>
  )
}

export function DoctorViewToggle({ viewMode, setViewMode }) {
  const { t } = useI18n()
  return (
    <div className="flex rounded-xl p-[3px] mb-4" style={{ background: '#F1F6FA' }}>
      <button onClick={() => setViewMode('list')} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[10px] text-[13px] font-extrabold" style={viewMode === 'list' ? { background: '#fff', color: '#15354F', boxShadow: '0 1px 4px rgba(21,53,79,0.1)' } : { color: '#64748B' }}>
        <List size={14} /> {t('doctors.listView', { defaultValue: 'List' })}
      </button>
      <button onClick={() => setViewMode('map')} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-[10px] text-[13px] font-extrabold" style={viewMode === 'map' ? { background: '#fff', color: '#15354F', boxShadow: '0 1px 4px rgba(21,53,79,0.1)' } : { color: '#64748B' }}>
        <MapIcon size={14} /> {t('doctors.mapView', { defaultValue: 'Map' })}
      </button>
    </div>
  )
}

export function DoctorFiltersSheet({
  showFilters, setShowFilters, resetFilters,
  specialtyFilter, setSpecialtyFilter, distanceFilter, setDistanceFilter,
  virtualActive, setVirtualActive, sortBy, setSortBy,
  patientHasInsurance, insuranceActive, setInsuranceActive,
  specialtyLocked = false, lockedSpecialtyLabel = null, lockedSpecialtyCaption = null,
  specialtyChoices = null, specialtyChoiceId = null, setSpecialtyChoiceId = null,
  showFilter = null, setShowFilter = null,
}) {
  const { t } = useI18n()
  return (
    <>
    {showFilters && createPortal((
      <div
        data-no-top-inset="bottom-anchored sheet (justify-end, rounded-t, pays the bottom inset)"
        className="fixed inset-0 z-[60] flex flex-col justify-end" style={{ background: 'rgba(15,31,46,0.38)' }} onClick={(e) => { if (e.target === e.currentTarget) setShowFilters(false) }}>
        <div className="bg-white rounded-t-[24px] px-5 pt-3 max-h-[88%] overflow-auto" style={{ boxShadow: '0 -8px 30px rgba(21,53,79,0.10)', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}>
          <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: '#D8E6F0' }} />
          <div className="flex items-center justify-between mb-4">
            <span className="text-[17px] font-extrabold" style={{ color: '#15354F' }}>{t('doctors.filters', { defaultValue: 'Filters' })}</span>
            <button onClick={resetFilters} className="text-[13px] font-extrabold" style={{ color: '#46A8F9' }}>{t('doctors.reset', { defaultValue: 'Reset' })}</button>
          </div>

          {/* ★ s106 (TestFlight b72, Dr. Rami: "Move the distance to the top"): Distance first, the miles
              before "Any" (the Doctors tab opens at 5 mi). */}
          <div data-testid="filters-distance-first">
          <FilterGroup label={t('doctors.distance', { defaultValue: 'Distance' })}>
            {DISTANCE_OPTIONS.map(d => (
              <FilterOpt key={d} active={distanceFilter === d} onClick={() => setDistanceFilter(d)}>{d} mi</FilterOpt>
            ))}
            <FilterOpt active={!distanceFilter} onClick={() => setDistanceFilter(null)}>{t('doctors.any', { defaultValue: 'Any' })}</FilterOpt>
          </FilterGroup>
          </div>

          {/* ★★ v242.105.0 — THE SHEET NOW READS `specialtyLocked`. It has ACCEPTED
              that prop since v242.103.0 and never once looked at it, so on the
              picker the sheet drew all 27 specialty chips with "All specialties"
              selected while the chip row three inches above it said the CASE had
              fixed the specialty. Two surfaces, one piece of state, opposite
              answers — and nothing warns, because a prop that is destructured and
              unread is indistinguishable from a prop that is honoured (D154's
              shape, arriving in the component that was written to avoid it).
              ★ ONE CHIP, NOT 27 DIMMED ONES. A control the patient can never
                operate is not information, it is furniture; and Reset must not be
                able to clear a choice the patient did not make. */}
          {/* ★ s101 (Build 67, Dr. Rami): "Specialty is locked but also should be open to PCP" /
              "Always allow PCP selection". With `specialtyChoices` the sheet offers the case's
              admin-assigned specialties and Primary care - always - instead of one locked chip. */}
          {Array.isArray(specialtyChoices) && specialtyChoices.length ? (
            <div className="mb-4" data-testid="picker-specialty-choices">
              <FilterGroup label={t('doctors.specialty', { defaultValue: 'Specialty' })}>
                {specialtyChoices.map((c, i) => (
                  <FilterOpt key={c.id} active={(specialtyChoiceId || specialtyChoices[0].id) === c.id} onClick={() => setSpecialtyChoiceId && setSpecialtyChoiceId(c.id)}>
                    {c.primary ? t('doctors.primaryCare', 'Primary care') : c.label}
                  </FilterOpt>
                ))}
              </FilterGroup>
              <p className="text-[11.5px] -mt-2" style={{ color: '#7C8FA3' }}>
                {t('doctors.specialtyFromCase', 'Suggested for your case')}
                {lockedSpecialtyCaption ? ` · ${lockedSpecialtyCaption}` : ''}
              </p>
            </div>
          ) : specialtyLocked ? (
            <div className="mb-4">
              <p className="text-[11px] font-extrabold tracking-wide uppercase mb-2" style={{ color: '#5F7A8E' }}>{t('doctors.specialty', { defaultValue: 'Specialty' })}</p>
              {/* Identical ink to an ACTIVE FilterOpt, deliberately: in this sheet
                  blue-on-white already means "selected", and the lock glyph is the
                  channel that says it cannot be changed. */}
              <span className="inline-flex items-center gap-1.5 text-[12.5px] font-bold rounded-[10px] px-3 py-2" style={{ border: '1px solid #46A8F9', color: '#46A8F9' }}>
                <Lock size={12} />
                {(typeof lockedSpecialtyLabel === 'string' && lockedSpecialtyLabel) || t('doctors.specialty', { defaultValue: 'Specialty' })}
              </span>
              <p className="text-[11.5px] mt-2" style={{ color: '#7C8FA3' }}>
                {t('doctors.specialtySetByCase', { defaultValue: 'Set by your case' })}
                {lockedSpecialtyCaption ? ` · ${lockedSpecialtyCaption}` : ''}
              </p>
            </div>
          ) : (
          <FilterGroup label={t('doctors.specialty', { defaultValue: 'Specialty' })}>
            <FilterOpt active={!specialtyFilter} onClick={() => setSpecialtyFilter(null)}>{t('doctors.allSpecialties', { defaultValue: 'All' })}</FilterOpt>
            {SPECIALTY_FILTERS.map(spec => (
              <FilterOpt key={spec} active={specialtyFilter === spec} onClick={() => setSpecialtyFilter(spec)}>{spec}</FilterOpt>
            ))}
          </FilterGroup>
          )}

          {typeof setShowFilter === 'function' && (
            <FilterGroup label={t('doctors.show', 'Show')}>
              <FilterOpt active={!showFilter || showFilter === 'all'} onClick={() => setShowFilter('all')}>{t('doctors.showAll', 'All doctors')}</FilterOpt>
              <FilterOpt active={showFilter === 'orella'} onClick={() => setShowFilter('orella')}>{t('doctors.showOrella', 'On Orella')}</FilterOpt>
              <FilterOpt active={showFilter === 'other'} onClick={() => setShowFilter('other')}>{t('doctors.showOther', 'Other doctors')}</FilterOpt>
            </FilterGroup>
          )}

          <FilterGroup label={t('doctors.visitType', { defaultValue: 'Visit type' })}>
            <FilterOpt active={!virtualActive} onClick={() => setVirtualActive(false)}>{t('doctors.inPerson', { defaultValue: 'In-person' })}</FilterOpt>
            <FilterOpt active={virtualActive} onClick={() => setVirtualActive(true)}>{t('doctors.virtual', { defaultValue: 'Virtual' })}</FilterOpt>
          </FilterGroup>

          <FilterGroup label={t('doctors.sortBy', { defaultValue: 'Sort by' })}>
            <FilterOpt active={sortBy === 'soonest'} onClick={() => setSortBy('soonest')}>{t('doctors.sortSoonest', { defaultValue: 'Soonest available' })}</FilterOpt>
            <FilterOpt active={sortBy === 'distance'} onClick={() => setSortBy('distance')}>{t('doctors.sortDistance', { defaultValue: 'Distance' })}</FilterOpt>
            <FilterOpt active={sortBy === 'rating'} onClick={() => setSortBy('rating')}>{t('doctors.sortRating', { defaultValue: 'Rating' })}</FilterOpt>
          </FilterGroup>

          {patientHasInsurance && (
            <div className="flex items-center justify-between py-3" style={{ borderTop: '1px solid #E7EEF4' }}>
              <span className="text-[13.5px] font-bold" style={{ color: '#15354F' }}>{t('doctors.inNetworkOnly', { defaultValue: "In my plan's network only" })}</span>
              <button onClick={() => setInsuranceActive(!insuranceActive)} className="w-[42px] h-[24px] rounded-full relative transition-colors" style={{ background: insuranceActive ? '#46A8F9' : '#D8E2EA' }}>
                <span className="absolute top-[2px] w-5 h-5 rounded-full bg-white transition-all" style={{ left: insuranceActive ? '20px' : '2px' }} />
              </button>
            </div>
          )}

          <button onClick={() => setShowFilters(false)} className="w-full rounded-[14px] py-3.5 text-[14.5px] font-extrabold mt-3" style={{ background: '#15354F', color: '#fff' }}>
            {/* ★ s106 (b72: "Always shows zero results - take this out"): the count was the Orella list
                only; the directory loads after the sheet closes. No number. */}
            {t('doctors.showResults', { defaultValue: 'Show results' })}
          </button>
        </div>
      </div>
    ), document.body)}
    </>
  )
}
